import React, { useState, useEffect, memo } from 'react';
import { Box, Typography, Button, CircularProgress, Autocomplete, TextField, Chip } from '@mui/material';
import { inputStyles } from './styles';
import { SKILLS_OPTIONS } from './constants';

const SkillsSection = memo(({ initialData, authAxios, setError, onUpdate }) => {
    const [skills, setSkills] = useState(initialData || []);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (initialData) {
            // Sanitize skills data
            const sanitized = Array.isArray(initialData) 
                ? initialData.filter(s => typeof s === 'string' && s.trim())
                : [];
            setSkills(sanitized);
        }
    }, [initialData]);

    const handleSave = async () => {
        setSaving(true);
        try {
            const response = await authAxios.patch('/self/', { skills });
            const savedSkills = response.data.skills || [];
            setSkills(savedSkills);
            onUpdate?.(savedSkills);
        } catch (error) {
            setError?.('Failed to save skills');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box sx={{ mb: 6 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontSize: '1.5rem', fontWeight: 600, color: '#111827' }}>Skills</Typography>
                <Button variant="contained" onClick={handleSave} disabled={saving}
                    sx={{ bgcolor: '#4f46e5', color: 'white', textTransform: 'none', fontWeight: 500, borderRadius: '6px', px: 3, '&:hover': { bgcolor: '#4338ca' } }}>
                    {saving ? <CircularProgress size={20} color="inherit" /> : 'Save'}
                </Button>
            </Box>
            <Autocomplete
                multiple
                freeSolo
                options={SKILLS_OPTIONS}
                value={skills}
                onChange={(e, newValue) => setSkills(newValue)}
                renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                        <Chip key={option} variant="outlined" label={option} {...getTagProps({ index })}
                            sx={{ borderColor: '#4f46e5', color: '#4f46e5', '& .MuiChip-deleteIcon': { color: '#4f46e5' } }} />
                    ))
                }
                renderInput={(params) => (
                    <TextField {...params} size="small" placeholder="Add skills (e.g., Python, React, AWS)" sx={inputStyles} />
                )}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '6px' } }}
            />
            <Typography variant="caption" sx={{ color: '#6b7280', mt: 1, display: 'block' }}>
                Type to search or add custom skills. Press Enter to add.
            </Typography>
        </Box>
    );
});

SkillsSection.displayName = 'SkillsSection';
export default SkillsSection;
